// Health Center backend payload translator.
//
// The backend health checks (services/health_checks/*.py) hardcode every
// human-readable string in Italian (name, category, title, details,
// metric keys). The cached state in MongoDB also persists those Italian
// strings, which makes a backend-side i18n refactor invasive AND slow
// to propagate (the TTL cache holds IT strings for up to 1h after the
// check re-runs).
//
// This module translates the payload CLIENT-SIDE based on the active
// IT Portal locale. When locale === 'IT' the original strings pass
// through unchanged. When locale === 'EN' (or any future language) we
// apply a deterministic translation using:
//  - `check_id`  → fixed English name (e.g. apple_cert → "Apple Wallet Certificate")
//  - `category`  → fixed English section header
//  - `title`     → regex pattern matching (handles dynamic numbers)
//  - `details`   → full-string lookup + fallback regex patterns
//  - `metrics`   → key/value substitution
//
// Unknown strings pass through unchanged so the operator can SEE what's
// missing (better than a generic "—") and we can extend this map.

const CHECK_NAMES_EN = {
  apple_cert: "Apple Wallet Certificate",
  google_wallet: "Google Wallet API",
  pdf_iframe_embed: "Wallet PDF Embedding (customer iframe)",
  sms_credit: "SMS Credit smshosting.it",
  email_smtp: "Transactional Email (Amazon SES SMTP)",
  emergent_llm: "AI Universal Key",
  aws_iam_key: "AWS IAM Access Keys",
  mongodb: "MongoDB",
  gdpr_retention: "GDPR Art. 17 — Retention",
};

// Names with dynamic suffix (hostname, domain) — handled via prefix replace.
const NAME_PREFIX_REPLACEMENTS_EN = [
  ["Certificato SSL ", "SSL Certificate "],
  ["Scadenza dominio ", "Domain Expiry "],
];

const CATEGORIES_EN = {
  "Apple Wallet": "Apple Wallet",
  "Wallet": "Wallet",
  "Dominio & Network": "Domain & Network",
  "Comunicazioni": "Communications",
  "AI & LLM": "AI & LLM",
  "Cloud & Storage": "Cloud & Storage",
  "Infrastruttura": "Infrastructure",
  "Compliance": "Compliance",
  "Unknown": "Unknown",
};

// Title patterns — order matters (more specific first).
const TITLE_PATTERNS_EN = [
  [/^OK · ancora (\d+) giorni di validità$/, (m) => `OK · ${m[1]} days of validity remaining`],
  [/^OK · ancora (\d+) giorni$/, (m) => `OK · ${m[1]} days remaining`],
  [/^OK · € ([\d.,]+) residui$/, (m) => `OK · € ${m[1]} remaining`],
  [/^OK · (\d+) PDF embeddabili cross-origin$/, (m) => `OK · ${m[1]} PDF(s) embeddable cross-origin`],
  [/^OK · (\d+) chiave\/i · max età (\d+) giorni$/, (m) => `OK · ${m[1]} key(s) · max age ${m[2]} days`],
  [/^Scade fra (\d+) giorni — RINNOVARE SUBITO$/, (m) => `Expires in ${m[1]} days — RENEW IMMEDIATELY`],
  [/^Scade fra (\d+) giorni — RINNOVARE$/, (m) => `Expires in ${m[1]} days — RENEW`],
  [/^Scade fra (\d+) giorni$/, (m) => `Expires in ${m[1]} days`],
  [/^Scadenza fra (\d+) giorni$/, (m) => `Expiring in ${m[1]} days`],
  [/^Rotazione SCADUTA da (\d+) giorni$/, (m) => `Rotation OVERDUE by ${m[1]} days`],
  [/^Rotazione fra (\d+) giorni$/, (m) => `Rotation in ${m[1]} days`],
  [/^Rotazione consigliata fra (\d+) giorni$/, (m) => `Rotation recommended in ${m[1]} days`],
  [/^SCADUTO — pass iOS invalidati$/, () => "EXPIRED — iOS passes invalidated"],
  [/^SCADUTO — TLS errato per i browser$/, () => "EXPIRED — TLS rejected by browsers"],
  [/^DOMINIO SCADUTO$/, () => "DOMAIN EXPIRED"],
  [/^Critico — solo € ([\d.,]+) residui$/, (m) => `Critical — only € ${m[1]} remaining`],
  [/^Sotto soglia — € ([\d.,]+)$/, (m) => `Below threshold — € ${m[1]}`],
  [/^Da monitorare — € ([\d.,]+)$/, (m) => `Monitor — € ${m[1]}`],
  [/^Credito esaurito — OTP SMS non più inviati$/, () => "Credit exhausted — SMS OTP no longer sent"],
  [/^Credito non disponibile via API$/, () => "Credit not available via API"],
  [/^AUTH OK · (.+)$/, (m) => `AUTH OK · ${m[1]}`],
  [/^Impossibile autenticarsi su SMTP$/, () => "Unable to authenticate to SMTP"],
  [/^Iframe BLOCCATO su (\d+)\/(\d+) PDF$/, (m) => `Iframe BLOCKED on ${m[1]}/${m[2]} PDF(s)`],
  [/^Issuer (.+) raggiungibile$/, (m) => `Issuer ${m[1]} reachable`],
  [/^Errore autenticazione Google Wallet$/, () => "Google Wallet authentication error"],
  [/^Service-account key non presente$/, () => "Service-account key not present"],
  [/^Certificato non ancora caricato$/, () => "Certificate not uploaded yet"],
  [/^WHOIS non disponibile in questo ambiente$/, () => "WHOIS not available in this environment"],
  [/^SMTP non configurato$/, () => "SMTP not configured"],
  [/^Integrazione SMS non configurata$/, () => "SMS integration not configured"],
  [/^Chiave AI universale non impostata$/, () => "Universal AI Key not set"],
  [/^Configurata · monitoring budget non automatizzabile$/, () => "Configured · automated budget monitoring not available"],
  [/^Credenziali AWS non impostate$/, () => "AWS credentials not set"],
  [/^Connesso · ([\d.,]+) clienti$/, (m) => `Connected · ${m[1]} customers`],
  [/^MongoDB irraggiungibile: (.+)$/, (m) => `MongoDB unreachable: ${m[1]}`],
  [/^Nessun record con retention scaduta$/, () => "No records past retention"],
  [/^(\d+) record con retention scaduta — revisione mensile dovuta$/, (m) => `${m[1]} records past retention — monthly review due`],
  [/^(\d+) record con retention scaduta$/, (m) => `${m[1]} records past retention`],
  [/^Impossibile recuperare il credito: (.+)$/, (m) => `Unable to fetch credit: ${m[1]}`],
  [/^Errore IAM: (.+)$/, (m) => `IAM error: ${m[1]}`],
  [/^Nessuna chiave restituita$/, () => "No keys returned"],
  [/^Impossibile verificare: (.+)$/, (m) => `Unable to verify: ${m[1]}`],
  [/^notAfter non parsabile: (.+)$/, (m) => `notAfter unparsable: ${m[1]}`],
  [/^BRAND_URL non configurato$/, () => "BRAND_URL not configured"],
  [/^Errore durante la verifica$/, () => "Error during check"],
  [/^Errore sconosciuto$/, () => "Unknown error"],
  [/^Errore$/, () => "Error"],
];

// Details translations — full-string lookups.
const DETAILS_EN = {
  "Antonio deve caricare il file .p12 dal pannello IT Portal → Apple Wallet → Pass Type ID Setup.":
    "Antonio must upload the .p12 file from IT Portal → Apple Wallet → Pass Type ID Setup.",
  "Posiziona il file `google_wallet_key.json` accanto al backend per abilitare l'integrazione Google Wallet.":
    "Place the `google_wallet_key.json` file next to the backend to enable Google Wallet integration.",
  "Imposta SMTP_HOST, SMTP_USERNAME, SMTP_PASSWORD in .env per abilitare le email.":
    "Set SMTP_HOST, SMTP_USERNAME, SMTP_PASSWORD in .env to enable email.",
  "Imposta AWS_ACCESS_KEY_ID + AWS_SECRET_ACCESS_KEY in .env per abilitare il monitoraggio delle chiavi (rotazione consigliata ogni 12 mesi).":
    "Set AWS_ACCESS_KEY_ID + AWS_SECRET_ACCESS_KEY in .env to enable key monitoring (rotation recommended every 12 months).",
  "Verifica le credenziali SMS_API_USER / SMS_API_KEY in .env":
    "Check SMS_API_USER / SMS_API_KEY credentials in .env",
  "Configura la Universal Key in backend/.env per abilitare le funzionalità AI (Sollecito Wallet, Reply Suggester, AI Monitor).":
    "Configure the Universal Key in backend/.env to enable AI features (Wallet Reminder, Reply Suggester, AI Monitor).",
  "Imposta BRAND_URL o REACT_APP_BACKEND_URL nelle env del backend per abilitare il monitoraggio.":
    "Set BRAND_URL or REACT_APP_BACKEND_URL in the backend env to enable monitoring.",
  "Ping OK · le collection critiche sono leggibili": "Ping OK · critical collections are readable",
  "Il monitor identifica i contratti e i record di compliance la cui retention legale è terminata. Nessuna cancellazione automatica: la revisione è manuale via pannello admin.":
    "The monitor identifies contracts and compliance records whose legal retention has ended. No automatic deletion: review is manual via the admin panel.",
  "Privacy Notice e Condizioni Contratto rispondono con frame-ancestors * e senza X-Frame-Options — l'iframe nel customer wallet funziona da qualsiasi dominio frontend.":
    "Privacy Notice and Contract Terms respond with frame-ancestors * and no X-Frame-Options — the iframe in the customer wallet works from any frontend domain.",
  "L'API di smshosting.it non restituisce il saldo per questo account. Il monitoraggio avverrà via tentativi di invio reali.":
    "The smshosting.it API does not return the balance for this account. Monitoring will occur via real send attempts.",
  "La chiave AI universale supporta Claude (text), Gemini (text + Nano Banana), OpenAI (text + GPT Image 1), Sora 2 e Whisper. Il budget residuo va verificato dal pannello TelcaVoIP. Quando si esaurisce, le feature AI si disabilitano (la chiamata ritorna 401/quota_exceeded). Si raccomanda di abilitare l'auto-top-up.":
    "The universal AI key supports Claude (text), Gemini (text + Nano Banana), OpenAI (text + GPT Image 1), Sora 2, and Whisper. Remaining budget must be verified from the TelcaVoIP panel. When exhausted, AI features are disabled (the call returns 401/quota_exceeded). Auto top-up is recommended.",
  "Almeno un PDF customer-facing non è più embeddabile cross-origin. Il cliente vedrà la schermata 'Impossibile aprire questa pagina in Firefox/Safari' nello step Privacy o nel ContractModal e non potrà completare l'onboarding. Verifica SecurityHeadersMiddleware in backend/server.py: il branch 'application/pdf' deve impostare frame-ancestors * e rimuovere X-Frame-Options.":
    "At least one customer-facing PDF can no longer be embedded cross-origin. The customer will see the 'Unable to open this page in Firefox/Safari' screen during the Privacy step or in the ContractModal and will not be able to complete onboarding. Verify SecurityHeadersMiddleware in backend/server.py: the 'application/pdf' branch must set frame-ancestors * and remove X-Frame-Options.",
  "Né la libreria python-whois né il CLI `whois` hanno restituito una expiration date. Su preview è normale (la rete del pod non permette query WHOIS in uscita); verifica in produzione o controlla manualmente lo stato del dominio dal registrar.":
    "Neither python-whois nor the `whois` CLI returned an expiration date. Normal on preview (pod network does not allow outbound WHOIS queries); verify in production or manually check the domain status with the registrar.",
};

// Details patterns — for dynamic strings.
const DETAILS_PATTERNS_EN = [
  [/^Pass Type ID `(.+)` · Scade (\d{4}-\d{2}-\d{2})$/, (m) => `Pass Type ID \`${m[1]}\` · Expires ${m[2]}`],
  [/^Service account: (.+)$/, (m) => `Service account: ${m[1]}`],
  [/^Soglia di alert: € ([\d.,]+) · SMS residui stimati: (.+)$/, (m) => `Alert threshold: € ${m[1]} · Estimated SMS remaining: ${m[2]}`],
  [/^TLS attivo · Username SMTP: (.+)$/, (m) => `TLS enabled · SMTP Username: ${m[1]}`],
  [/^TLS disattivo · Username SMTP: (.+)$/, (m) => `TLS disabled · SMTP Username: ${m[1]}`],
  [/^Subject CN: (.+) · Issuer: (.+)$/, (m) => `Subject CN: ${m[1]} · Issuer: ${m[2]}`],
  [/^Verificato via WHOIS · Scadenza ufficiale (.+)$/, (m) => `Verified via WHOIS · Official expiry ${m[1]}`],
  [/^Politica: rotazione ogni (\d+) giorni · (\d+) chiave\/i attive$/, (m) => `Policy: rotation every ${m[1]} days · ${m[2]} active key(s)`],
  [/(.+) · Suggerimento: la policy della chiave deve includere `(.+)` per il proprio utente\.$/, (m) => `${m[1]} · Hint: the key policy must include \`${m[2]}\` for the calling user.`],
  [/^L'API IAM ha risposto, ma non ha restituito access keys per questo utente\.$/, () => "The IAM API responded but returned no access keys for this user."],
  [/^Eccezione: (.+)$/, (m) => `Exception: ${m[1]}`],
];

// Metrics keys mapping.
const METRICS_KEYS_EN = {
  "Giorni residui": "Days remaining",
  "Caricato da": "Uploaded by",
  "Caricato il": "Uploaded on",
  "SHA-256": "SHA-256",
  "Issuer ID": "Issuer ID",
  "Service account": "Service account",
  "Project": "Project",
  "Credito (€)": "Credit (€)",
  "SMS residui": "SMS remaining",
  "Soglia warn (€)": "Warn threshold (€)",
  "Host": "Host",
  "Porta": "Port",
  "TLS": "TLS",
  "Scadenza": "Expiry",
  "Hostname verificato": "Hostname verified",
  "Issuer": "Issuer",
  "Dominio": "Domain",
  "Expiration date": "Expiration date",
  "Host monitorato": "Monitored host",
  "Informativa Privacy": "Privacy Notice",
  "Condizioni Contratto": "Contract Terms",
  "Soglia rotazione (gg)": "Rotation threshold (days)",
  "Chiave": "Key",
  "Provider supportati": "Supported providers",
  "Auto top-up": "Auto top-up",
  "Clienti": "Customers",
  "Contratti firmati": "Signed contracts",
  "Eventi audit admin": "Admin audit events",
  "Contratti oltre retention 10y": "Contracts past 10y retention",
  "Compliance oltre retention 5y": "Compliance past 5y retention",
};

// Metrics value substitutions (whole-cell exact matches).
const METRICS_VALUES_EN = {
  "Attivo": "Enabled",
  "Disattivo": "Disabled",
  "OK": "OK",
};

// Metrics value patterns — keep most of the string intact, replace
// individual Italian words.
const METRICS_VALUE_PATTERNS_EN = [
  [/^KO · CSP frame-ancestors troppo restrittivo \(iframe bloccato cross-origin\)$/, () => "KO · CSP frame-ancestors too restrictive (iframe blocked cross-origin)"],
  [/^KO · X-Frame-Options ancora presente: (.+) \(deve essere assente sui PDF\)$/, (m) => `KO · X-Frame-Options still present: ${m[1]} (must be absent on PDFs)`],
  [/^KO · Content-Type non PDF: (.+)$/, (m) => `KO · Content-Type not PDF: ${m[1]}`],
  [/^KO · errore sconosciuto$/, () => "KO · unknown error"],
  [/^KO · (.+)$/, (m) => `KO · ${m[1]}`],
  [/^errore sconosciuto$/, () => "unknown error"],
  [/^Configurabile dal pannello TelcaVoIP$/, () => "Configurable from the TelcaVoIP panel"],
  [/^(Active|Inactive) · (\d+)gg$/, (m) => `${m[1]} · ${m[2]}d`],
];

const applyPattern = (str, patterns) => {
  if (!str) return str;
  for (const [re, fn] of patterns) {
    const m = str.match(re);
    if (m) return fn(m);
  }
  return str;
};

const translateName = (name, checkId) => {
  if (CHECK_NAMES_EN[checkId]) return CHECK_NAMES_EN[checkId];
  for (const [prefix, repl] of NAME_PREFIX_REPLACEMENTS_EN) {
    if (name?.startsWith(prefix)) return name.replace(prefix, repl);
  }
  return name;
};

const translateCategory = (category) => CATEGORIES_EN[category] || category;
const translateTitle = (title) => applyPattern(title, TITLE_PATTERNS_EN);
const translateDetails = (details) => {
  if (!details) return details;
  if (DETAILS_EN[details]) return DETAILS_EN[details];
  return applyPattern(details, DETAILS_PATTERNS_EN);
};

const translateMetrics = (metrics) => {
  if (!metrics || typeof metrics !== "object") return metrics;
  const out = {};
  for (const [k, v] of Object.entries(metrics)) {
    const newKey = METRICS_KEYS_EN[k] || k;
    let newVal = v;
    if (typeof v === "string") {
      if (METRICS_VALUES_EN[v]) newVal = METRICS_VALUES_EN[v];
      else newVal = applyPattern(v, METRICS_VALUE_PATTERNS_EN);
    }
    out[newKey] = newVal;
  }
  return out;
};

/**
 * Translate a Health Center check payload to the active locale.
 * When locale === 'IT' (or any unsupported locale) the input is
 * returned unchanged.
 */
export const translateHealthCheck = (check, locale) => {
  if (!check || locale === "IT") return check;
  if (locale !== "EN") return check;  // EN is currently the only target
  return {
    ...check,
    name: translateName(check.name, check.check_id),
    category: translateCategory(check.category),
    title: translateTitle(check.title),
    details: translateDetails(check.details),
    metrics: translateMetrics(check.metrics),
  };
};

/**
 * Translate a top-level category name (used as section header).
 * Pass-through for IT locale.
 */
export const translateHealthCategory = (category, locale) =>
  (locale === "EN" ? translateCategory(category) : category);
